// src/lib/kinema/konva/renderers/VideoRenderer.ts
import Konva from 'konva';
import type { KonvaRenderer } from './types';
import type { BaseLayerData } from '../../core/types';
import { attachDebugListeners } from '../../audio/AudioReconciler';
import { mediaRegistry } from '../../audio/mediaRegistry';

const videoElements = new Map<string, HTMLVideoElement>();

export const VideoRenderer: KonvaRenderer = {
  create(layer: BaseLayerData): Konva.Node {
    const group = new Konva.Group({ width: layer.width, height: layer.height });

    const placeholder = new Konva.Rect({
      width: layer.width,
      height: layer.height,
      fill: '#e2e8f0',
      cornerRadius: 2,
    });
    group.add(placeholder);

    const konvaImage = new Konva.Image({
      image: undefined as unknown as HTMLImageElement,
      width: layer.width,
      height: layer.height,
    });
    group.add(konvaImage);

    if (layer.src) {
      const video = createVideoElement(layer.src);
      videoElements.set(layer.id, video);
      mediaRegistry.set(layer.id, video);
      attachLifecycle(video, group, konvaImage, placeholder);
      attachDebugListeners(video, `video:${layer.id.slice(-6)}`);
    }

    return group;
  },

  update(node: Konva.Node, layer: BaseLayerData, changed: Record<string, boolean>, time?: number, isPlaying: boolean = false): void {
    const group = node as Konva.Group;
    const placeholder = group.children?.[0] as Konva.Rect | undefined;
    const konvaImage = group.children?.[1] as Konva.Image | undefined;

    if (changed.src && layer.src) {
      const oldVideo = videoElements.get(layer.id);
      if (oldVideo) { oldVideo.pause(); oldVideo.removeAttribute('src'); oldVideo.load(); }

      const video = createVideoElement(layer.src);
      videoElements.set(layer.id, video);
      mediaRegistry.set(layer.id, video);
      attachLifecycle(video, group, konvaImage, placeholder);
      attachDebugListeners(video, `video:${layer.id.slice(-6)}`);
    }

    if (changed.width) {
      group.setAttr('width', layer.width);
      if (placeholder) placeholder.width(layer.width);
      if (konvaImage) konvaImage.width(layer.width);
    }
    if (changed.height) {
      group.setAttr('height', layer.height);
      if (placeholder) placeholder.height(layer.height);
      if (konvaImage) konvaImage.height(layer.height);
    }

    // Playback control (play/pause/seek/volume) lives in AudioReconciler
    // which subscribes to editor state directly — running it here as well
    // would introduce a React-render delay before video pause/play takes
    // effect. We still need the unused `time` and `isPlaying` params to
    // satisfy the renderer signature.
    void time;
    void isPlaying;
  },

  destroy(node: Konva.Node): void {
    const layerId = node.getAttr('layerId');
    if (layerId) {
      const video = videoElements.get(layerId);
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
        videoElements.delete(layerId);
      }
      mediaRegistry.delete(layerId);
    }
    node.destroy();
  },
};

function attachLifecycle(
  video: HTMLVideoElement,
  group: Konva.Group,
  konvaImage: Konva.Image | undefined,
  placeholder: Konva.Rect | undefined,
): void {
  video.addEventListener('loadeddata', () => {
    konvaImage?.image(video);
    if (placeholder) placeholder.visible(false);
    group.getLayer()?.batchDraw();
  });
  // Keep canvas in sync with the video frame on seeks AND while playing.
  video.addEventListener('seeked', () => group.getLayer()?.batchDraw());
  video.addEventListener('timeupdate', () => group.getLayer()?.batchDraw());
}

function createVideoElement(src: string): HTMLVideoElement {
  const video = document.createElement('video');
  video.crossOrigin = 'Anonymous';
  video.src = src;
  video.playsInline = true;
  video.preload = 'auto';
  video.load();
  return video;
}
